import { Button, Result, Space } from '@bookora/ui';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth, useSessionStore } from './token-store';

/** The platform console, where a super-admin's work actually is. */
const ADMIN_CONSOLE_URL = String(
  import.meta.env.VITE_ADMIN_URL ?? 'https://admin.bookora.net',
).replace(/\/+$/, '');

/**
 * Route guard for everything behind the shell.
 *
 * Signed out → `/login?next=…`, so a bookmarked patient file returns the user
 * to that file after they authenticate.
 *
 * Signed in with **no clinic membership** → `/signup`, unless the account is a
 * super-admin (which belongs on the platform console — that branch gets a card
 * with a link there). No membership is a real state for a normal account: a
 * signup that verified its phone but never created a clinic, a staff member
 * whose only membership was deactivated, or someone who signed in with the
 * wrong number. The signup page tells them apart from a fresh signup: it says
 * which number is signed in, that staff join by invitation, and always offers
 * «ورود با شمارهٔ دیگه». Without this redirect they would land on a dashboard
 * where every request 403s with `CLINIC_CONTEXT_MISSING`, which reads like an
 * outage rather than an account problem.
 *
 * This is a routing convenience, not a security boundary. Every endpoint the
 * screens behind it call is independently authenticated and permission-checked.
 */
export function AuthGate() {
  const { isAuthenticated, memberships, user } = useAuth();
  const clearSession = useSessionStore((state) => state.clearSession);
  const location = useLocation();

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    const suffix = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${suffix}`} replace />;
  }

  if (memberships.length === 0) {
    // A super-admin belongs on the platform console, not here — a dead end with
    // an explanation. Anyone else with no clinic is a signup that has not
    // finished: send them to the wizard to create one (or accept an invite and
    // sign in again).
    if (!user?.isSuperAdmin) {
      return <Navigate to="/signup" replace />;
    }

    // On the sign-in ground, centred: there is no shell around this card, and
    // a lone result pinned to the top of a blank page read as a broken load.
    return (
      <div className="bk-login">
        <Result
          className="bk-login__card"
          status="warning"
          title="هنوز عضو هیچ کلینیکی نیستید"
          subTitle="این حساب مربوط به مدیریت بوکوراست و به پنل کلینیک دسترسی نداره. وارد پنل مدیریت بوکورا بشید."
          extra={
            <Space wrap>
              <Button type="primary" href={ADMIN_CONSOLE_URL}>
                رفتن به پنل مدیریت
              </Button>
              <Button
                onClick={() => {
                  clearSession();
                  window.location.assign('/login');
                }}
              >
                خروج از حساب
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  return <Outlet />;
}
